import { IRoutingProvider } from '../routing.interface';
import { Coordinates, RoutingOptions, RouteResult, TransportMode } from '../routing.types';
import { decodeGooglePolyline } from '../polyline.utils';
import { API_CONFIG } from '../../../config/api';

/**
 * Provider de routing utilisant Google Directions API via le backend proxy
 */
export class GoogleRoutingProvider implements IRoutingProvider {
  readonly name = 'google';

  /**
   * Mapping des modes de transport vers les modes Google
   */
  private getModeParam(mode?: TransportMode): string {
    switch (mode) {
      case 'driving':
        return 'driving';
      case 'cycling':
        return 'bicycling';
      case 'walking':
      default:
        return 'walking';
    }
  }

  async getRoute(
    origin: Coordinates,
    destination: Coordinates,
    options?: RoutingOptions
  ): Promise<RouteResult> {
    const originStr = `${origin[0]},${origin[1]}`;
    const destinationStr = `${destination[0]},${destination[1]}`;
    const mode = this.getModeParam(options?.mode);

    const token = localStorage.getItem('accessToken');
    const url = `${API_CONFIG.baseURL}/journeys/directions?origin=${originStr}&destination=${destinationStr}&mode=${mode}`;

    console.log('[GoogleRoutingProvider] Fetching route:', { origin: originStr, destination: destinationStr, mode, url });

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('[GoogleRoutingProvider] Response status:', response.status);

      if (!response.ok) {
        console.error('[GoogleRoutingProvider] HTTP error:', response.status, response.statusText);
        return this.createFallbackRoute(origin, destination);
      }

      const data = await response.json();
      console.log('[GoogleRoutingProvider] Response data status:', data.status, 'fallback:', data.fallback);

      // Vérifier si fallback est nécessaire
      if (data.fallback || data.error) {
        console.warn('[GoogleRoutingProvider] Using fallback straight line:', data.error);
        return this.createFallbackRoute(origin, destination);
      }

      if (!data.routes || data.routes.length === 0) {
        console.warn('[GoogleRoutingProvider] No routes found, using fallback');
        return this.createFallbackRoute(origin, destination);
      }

      const route = data.routes[0];
      const coordinates: Coordinates[] = [];

      // Décoder toutes les polylines des steps
      route.legs.forEach((leg: any) => {
        leg.steps.forEach((step: any) => {
          const decoded = decodeGooglePolyline(step.polyline.points);
          coordinates.push(...decoded);
        });
      });

      console.log('[GoogleRoutingProvider] Decoded coordinates count:', coordinates.length);

      // Extraire distance et durée du premier leg
      const leg = route.legs[0];
      const distance = leg.distance?.value || 0; // en mètres
      const duration = leg.duration?.value || 0; // en secondes

      return {
        coordinates,
        distance,
        duration,
        provider: this.name,
      };
    } catch (error) {
      console.error('[GoogleRoutingProvider] Fetch error:', error);
      return this.createFallbackRoute(origin, destination);
    }
  }

  /**
   * Crée une route fallback (ligne droite) si l'API échoue
   */
  private createFallbackRoute(
    origin: Coordinates,
    destination: Coordinates
  ): RouteResult {
    // Calcul distance à vol d'oiseau (Haversine)
    const R = 6371000;
    const dLat = ((destination[0] - origin[0]) * Math.PI) / 180;
    const dLng = ((destination[1] - origin[1]) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((origin[0] * Math.PI) / 180) *
        Math.cos((destination[0] * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    // Estimation durée (marche ~5km/h)
    const duration = (distance / 1000 / 5) * 3600;

    return {
      coordinates: [origin, destination],
      distance,
      duration,
      provider: `${this.name}-fallback`,
    };
  }
}
